import { SectionHead } from './SectionHead';
import { Field } from './Field';
import {
  FormHeading, PageTitle, PageSubtitle,
  Section, SectionDesc, Grid, FullWidth,
  TextInput, RequiredStar,
  PrefillLabelRow, PrefillLabel, PrefillBadge, PrefillBox,
  CommissionBox, TermsLabel, CheckboxEl, TermsText, TermsLink,
  ErrorBanner, SubmitBtn,
} from './styles';

export function VenueAgreementForm({ venueInfo, f, accept, err, triedSubmit, isFormReady, submitting, submitError, change, toggleAccept, handleSubmit, r }) {
  const country = venueInfo.country;
  const phonePh = country === 'NZ' ? '+6X 2X XXX XXXX' : '+6X 4XX XXX XXX';
  const hasErrors = Object.keys(err).length > 0;

  return (
    <>
      <FormHeading>
        <PageTitle>Venue Agreement</PageTitle>
        <PageSubtitle>
          Confirm your venue business details and provide the information below so we can set you
          up for gift card redemption payouts.
        </PageSubtitle>
      </FormHeading>

      {/* Venue */}
      <Section>
        <SectionHead title="Venue" bullet="#f04e23" />
        <Grid>
          <FullWidth>
            <PrefillLabelRow>
              <PrefillLabel>VENUE</PrefillLabel>
              <PrefillBadge>Pre-filled</PrefillBadge>
            </PrefillLabelRow>
            <PrefillBox>{venueInfo.venueDisplayName}</PrefillBox>
          </FullWidth>

          <div ref={r('bizName')}>
            <Field id="bizName" label="REGISTERED BUSINESS NAME" required error={err.bizName}>
              <TextInput
                id="bizName" type="text" value={f.bizName} onChange={change('bizName')}
                $err={!!err.bizName} aria-required="true"
              />
            </Field>
          </div>

          <div ref={r('abn')}>
            <Field id="abn" label={country === 'NZ' ? 'NZBN' : 'ABN'} required error={err.abn}>
              <TextInput
                id="abn" type="text" inputMode="numeric" value={f.abn} onChange={change('abn')}
                placeholder={country === 'NZ' ? '13 digit NZBN' : '11 digit ABN'}
                $err={!!err.abn} aria-required="true"
              />
            </Field>
          </div>
        </Grid>
      </Section>

      {/* Authorised Representative */}
      <Section>
        <SectionHead title="Authorised Representative" bullet="#f04e23" compact />
        <SectionDesc>This is your authorised contact for agreement and accounting matters.</SectionDesc>
        <Grid>
          <div ref={r('repFirst')}>
            <Field id="repFirst" label="FIRST NAME" required error={err.repFirst}>
              <TextInput id="repFirst" type="text" value={f.repFirst} onChange={change('repFirst')} placeholder="First name" $err={!!err.repFirst} aria-required="true" />
            </Field>
          </div>
          <div ref={r('repLast')}>
            <Field id="repLast" label="SURNAME" required error={err.repLast}>
              <TextInput id="repLast" type="text" value={f.repLast} onChange={change('repLast')} placeholder="Surname" $err={!!err.repLast} aria-required="true" />
            </Field>
          </div>
          <div ref={r('repEmail')}>
            <Field id="repEmail" label="EMAIL ADDRESS" required error={err.repEmail}>
              <TextInput id="repEmail" type="email" value={f.repEmail} onChange={change('repEmail')} placeholder="name@venue.com" $err={!!err.repEmail} aria-required="true" />
            </Field>
          </div>
          <div ref={r('repPhone')}>
            <Field id="repPhone" label="PHONE NUMBER" required error={err.repPhone}>
              <TextInput id="repPhone" type="tel" value={f.repPhone} onChange={change('repPhone')} placeholder={phonePh} $err={!!err.repPhone} aria-required="true" />
            </Field>
          </div>
        </Grid>
      </Section>

      {/* Operations Contact */}
      <Section>
        <SectionHead title="Operations Contact" bullet="#3a4253" compact />
        <SectionDesc>Receives the redemption portal login and onboarding info. Leave blank if same as above.</SectionDesc>
        <Grid>
          <Field id="opsFirst" label="FIRST NAME">
            <TextInput id="opsFirst" type="text" value={f.opsFirst} onChange={change('opsFirst')} placeholder="First name" />
          </Field>
          <Field id="opsLast" label="SURNAME">
            <TextInput id="opsLast" type="text" value={f.opsLast} onChange={change('opsLast')} placeholder="Surname" />
          </Field>
          <div ref={r('opsEmail')}>
            <Field id="opsEmail" label="EMAIL ADDRESS" error={err.opsEmail}>
              <TextInput id="opsEmail" type="email" value={f.opsEmail} onChange={change('opsEmail')} placeholder="name@venue.com" $err={!!err.opsEmail} />
            </Field>
          </div>
          <div ref={r('opsPhone')}>
            <Field id="opsPhone" label="PHONE NUMBER" error={err.opsPhone}>
              <TextInput id="opsPhone" type="tel" value={f.opsPhone} onChange={change('opsPhone')} placeholder={phonePh} $err={!!err.opsPhone} />
            </Field>
          </div>
        </Grid>
      </Section>

      {/* Address */}
      <Section>
        <SectionHead title="Address" bullet="#f04e23" compact />
        <SectionDesc>This is the address we will use for remittances.</SectionDesc>
        <Grid $min="160px">
          <FullWidth ref={r('remitEmail')}>
            <Field id="remitEmail" label="REMITTANCE EMAIL ADDRESS" required error={err.remitEmail}>
              <TextInput id="remitEmail" type="email" value={f.remitEmail} onChange={change('remitEmail')} placeholder="name@venue.com" $err={!!err.remitEmail} aria-required="true" />
            </Field>
          </FullWidth>
          <FullWidth ref={r('street')}>
            <Field id="street" label="ADDRESS LINE 1" required error={err.street}>
              <TextInput id="street" type="text" value={f.street} onChange={change('street')} placeholder="Street number and name" $err={!!err.street} aria-required="true" />
            </Field>
          </FullWidth>
          <div ref={r('city')}>
            <Field id="city" label="CITY" required error={err.city}>
              <TextInput id="city" type="text" value={f.city} onChange={change('city')} placeholder="City" $err={!!err.city} aria-required="true" />
            </Field>
          </div>
          {country === 'AU' && (
            <div ref={r('state')}>
              <Field id="state" label="STATE" required error={err.state}>
                <TextInput id="state" type="text" value={f.state} onChange={change('state')} placeholder="State" $err={!!err.state} aria-required="true" />
              </Field>
            </div>
          )}
          <div ref={r('postcode')}>
            <Field id="postcode" label="POSTCODE" required error={err.postcode}>
              <TextInput id="postcode" type="text" inputMode="numeric" value={f.postcode} onChange={change('postcode')} placeholder="Postcode" $err={!!err.postcode} aria-required="true" />
            </Field>
          </div>
        </Grid>
      </Section>

      {/* Bank Details */}
      <Section>
        <SectionHead title="Bank Details" bullet="#f04e23" compact />
        <SectionDesc>We store this information securely to issue your gift card redemption payouts.</SectionDesc>
        {country === 'AU' ? (
          <Grid>
            <div ref={r('bsb')}>
              <Field id="bsb" label="BSB" required error={err.bsb} tight>
                <TextInput id="bsb" type="text" inputMode="numeric" value={f.bsb} onChange={change('bsb')} placeholder="123-456" $err={!!err.bsb} aria-required="true" />
              </Field>
            </div>
            <div ref={r('account')}>
              <Field id="account" label="ACCOUNT NUMBER" required error={err.account} tight>
                <TextInput id="account" type="text" inputMode="numeric" value={f.account} onChange={change('account')} placeholder="123456789" $err={!!err.account} aria-required="true" />
              </Field>
            </div>
          </Grid>
        ) : (
          <div ref={r('nzAccount')}>
            <Field id="nzAccount" label="BANK ACCOUNT" required error={err.nzAccount} tight>
              <TextInput id="nzAccount" type="text" inputMode="numeric" value={f.nzAccount} onChange={change('nzAccount')} placeholder="BB-bbbb-AAAAAAA-SS" $err={!!err.nzAccount} aria-required="true" />
            </Field>
          </div>
        )}
      </Section>

      {/* Terms */}
      <Section>
        <SectionHead title="Terms" bullet="#f04e23" />
        <CommissionBox>
          Our commission deducted from gift card redemptions is{' '}
          <strong style={{ fontWeight: 700, color: '#fff' }}>
            {venueInfo.commissionRate}%
          </strong>
        </CommissionBox>
        <div ref={r('accept')}>
          <TermsLabel $err={!!err.accept} onClick={toggleAccept}>
            <CheckboxEl
              $checked={accept}
              $err={!!err.accept}
              role="checkbox"
              aria-checked={accept}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleAccept(); }
              }}
            >
              {accept ? '✓' : ''}
            </CheckboxEl>
            <TermsText>
              Submitting this form constitutes your agreement to our{' '}
              <TermsLink
                href="https://bestgiftgroup.com/terms-conditions-vendors"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                aria-label="standard terms and conditions (opens in new tab)"
              >
                standard terms and conditions
              </TermsLink>
              . <RequiredStar>*</RequiredStar>
            </TermsText>
          </TermsLabel>
          {err.accept && <p style={{ color: '#ff6a4d', fontSize: 13, marginTop: 6 }}>{err.accept}</p>}
        </div>
      </Section>

      {triedSubmit && hasErrors && (
        <ErrorBanner role="alert" aria-live="assertive">
          Please fix the highlighted fields before submitting.
        </ErrorBanner>
      )}

      {submitError && (
        <ErrorBanner role="alert" aria-live="assertive">
          {submitError}
        </ErrorBanner>
      )}

      <SubmitBtn type="button" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Submitting…' : 'Submit agreement'}
      </SubmitBtn>
    </>
  );
}
